import * as React from "react";
import { Link } from "wouter";
import type { LucideIcon } from "lucide-react";
import { ChevronRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

/**
 * ActionCard - Icon-first quick action card with semantic gradients
 *
 * Design Principles:
 * - Icon-first visual hierarchy
 * - 3 semantic gradients for visual clarity (primary, success, neutral)
 * - Smooth hover animations revealing additional context
 * - Swiss minimalism with modern polish
 * - Performant CSS-only animations
 * - Full design token compliance across all 4 themes
 *
 * Semantic Gradient Mapping:
 * - primary: Main CTAs, important actions, data operations
 * - success: Positive actions, uploads, completed states
 * - neutral: Secondary actions, settings, informational
 *
 * @example
 * ```tsx
 * <ActionCard
 *   icon={Building2}
 *   title="Manage Companies"
 *   description="View and manage your companies"
 *   href="/companies"
 *   gradient="primary"
 *   onActionClick={(title) => analytics.track('quick_action_clicked', { action: title })}
 * />
 * ```
 */

export interface ActionCardProps {
  /** Lucide icon component */
  icon: LucideIcon;
  /** Card title (primary action) */
  title: string;
  /** Description text (appears below title) */
  description: string;
  /** Navigation URL */
  href: string;
  /** Semantic gradient - primary (CTAs), success (positive), neutral (secondary) */
  gradient?: "primary" | "success" | "neutral";
  /** Optional click analytics callback */
  onActionClick?: (actionName: string) => void;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Semantic gradient presets using design tokens
 *
 * Simplified from 7 variants to 3 semantic gradients:
 * - primary: Brand blue, CTAs, important actions
 * - success: Green, positive outcomes, upload/completion
 * - neutral: Muted, secondary actions, settings
 *
 * All gradients use CSS custom properties for theme consistency
 * across Light, Dark, Sakura, and Andes themes.
 */
const gradientPresets = {
  primary:
    "from-primary/10 to-primary/5 hover:from-primary/20 hover:to-primary/10",
  success:
    "from-[var(--success)]/10 to-[var(--success)]/5 hover:from-[var(--success)]/20 hover:to-[var(--success)]/10",
  neutral: "from-muted/20 to-muted/10 hover:from-muted/30 hover:to-muted/20",
} as const;

/**
 * Semantic icon color variants using design tokens
 * Maps to semantic color tokens for consistent theming
 */
const iconColorVariants = {
  primary: "text-primary",
  success: "text-[var(--success)]",
  neutral: "text-muted-foreground",
} as const;

export function ActionCard({
  icon: Icon,
  title,
  description,
  href,
  gradient = "primary",
  onActionClick,
  className,
}: ActionCardProps) {
  const handleClick = () => {
    if (onActionClick) {
      onActionClick(title);
    }
  };

  return (
    <Link href={href} onClick={handleClick}>
      <Card
        className={cn(
          // Base styles
          "group relative overflow-hidden cursor-pointer h-full",
          // Transitions - smooth and performant
          "transition-all duration-300 ease-out",
          // Hover effects - subtle lift and shadow increase
          "hover:shadow-lg hover:-translate-y-1",
          // Focus styles for keyboard navigation (a11y)
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          className
        )}
      >
        {/* Gradient Background Layer */}
        <div
          className={cn(
            "absolute inset-0 bg-gradient-to-br transition-all duration-300",
            gradientPresets[gradient]
          )}
          aria-hidden="true"
        />

        <CardContent className="relative p-6">
          <div className="flex flex-col items-start gap-4">
            {/* Icon Container - Prominent, icon-first design */}
            <div
              className={cn(
                "rounded-xl p-3 transition-all duration-300",
                // Background with subtle transparency
                "bg-background/50 group-hover:bg-background/80",
                // Shadow grows on hover
                "shadow-sm group-hover:shadow-md",
                // Scale up slightly on hover for micro-interaction
                "group-hover:scale-105"
              )}
            >
              <Icon
                className={cn(
                  "h-6 w-6 transition-colors duration-300",
                  iconColorVariants[gradient]
                )}
                strokeWidth={2}
                aria-hidden="true"
              />
            </div>

            {/* Text Content */}
            <div className="space-y-1 flex-1">
              <h3 className="text-h5 font-semibold text-foreground leading-snug">
                {title}
              </h3>
              <p className="text-body-small text-muted-foreground line-clamp-2 leading-relaxed">
                {description}
              </p>
            </div>

            {/* Hover Indicator - Appears on hover to indicate interactivity */}
            <div
              className={cn(
                "flex items-center gap-1 text-body-small font-medium",
                "opacity-0 group-hover:opacity-100 transition-opacity duration-300",
                iconColorVariants[gradient]
              )}
            >
              <span className="sr-only">Navigate to {title}</span>
              <ChevronRight
                className="h-4 w-4"
                strokeWidth={2.5}
                aria-hidden="true"
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
